<template>
  <div class="nas-sw-page">
    <header class="nas-sw-hero">
      <router-link to="/product/storage" class="nas-sw-back">← 返回存储产品</router-link>
    </header>

    <div class="nas-sw-deck">
      <article
        v-for="(slide, si) in slides"
        :key="si"
        class="nas-sw-slide"
        :data-slide-type="slide.type"
      >
        <!-- 封面 -->
        <template v-if="slide.type === 'cover'">
          <div class="nas-sw-slide-inner nas-sw-slide--cover">
            <h1 class="nas-sw-cover-title">{{ slide.title }}</h1>
          </div>
        </template>

        <!-- 章节分隔（类似 PPT 章节页） -->
        <template v-else-if="slide.type === 'section'">
          <div class="nas-sw-slide-inner nas-sw-slide--section">
            <span class="nas-sw-section-page" aria-hidden="true">{{ slide.page }}</span>
            <h2 class="nas-sw-section-title">{{ slide.title }}</h2>
          </div>
        </template>

        <!-- 编号要点 01 02 03 -->
        <template v-else-if="slide.type === 'numbered'">
          <div class="nas-sw-slide-inner nas-sw-slide--numbered">
            <header class="nas-sw-slide-head">
              <h2 class="nas-sw-slide-title">{{ slide.title }}</h2>
            </header>
            <div class="nas-sw-numbered-grid">
              <div
                v-for="(b, bi) in slide.blocks"
                :key="bi"
                class="nas-sw-numbered-card"
              >
                <span class="nas-sw-numbered-num">{{ b.num }}</span>
                <h3 class="nas-sw-numbered-headline">{{ b.headline }}</h3>
                <p class="nas-sw-numbered-body">{{ b.body }}</p>
              </div>
            </div>
          </div>
        </template>

        <!-- 大标题 + 可选横幅 + 多组小标题+要点 -->
        <template v-else-if="slide.type === 'grouped'">
          <div class="nas-sw-slide-inner nas-sw-slide--grouped">
            <header class="nas-sw-slide-head">
              <h2 class="nas-sw-slide-title">{{ slide.title }}</h2>
            </header>
            <p v-if="slide.banner" class="nas-sw-group-banner">{{ slide.banner }}</p>
            <div class="nas-sw-group-sections">
              <section
                v-for="(sec, sei) in slide.sections"
                :key="sei"
                class="nas-sw-group-block"
              >
                <h3 class="nas-sw-group-subtitle">{{ sec.subtitle }}</h3>
                <ul v-if="sec.bullets.length" class="nas-sw-bullet-list">
                  <li v-for="(b, bi) in sec.bullets" :key="bi">{{ b }}</li>
                </ul>
              </section>
            </div>
          </div>
        </template>

        <!-- 成对：小标题 + 段落 -->
        <template v-else-if="slide.type === 'pairs'">
          <div class="nas-sw-slide-inner nas-sw-slide--pairs">
            <header class="nas-sw-slide-head">
              <h2 class="nas-sw-slide-title">{{ slide.title }}</h2>
            </header>
            <div class="nas-sw-pairs-grid">
              <div
                v-for="(p, pi) in slide.pairs"
                :key="pi"
                class="nas-sw-pair-card"
              >
                <h3 class="nas-sw-pair-sub">{{ p.subtitle }}</h3>
                <p class="nas-sw-pair-text">{{ p.text }}</p>
              </div>
            </div>
          </div>
        </template>

        <!-- 无法结构化时：标题 + 条目列表 -->
        <template v-else>
          <div class="nas-sw-slide-inner nas-sw-slide--fallback">
            <header class="nas-sw-slide-head">
              <h2 class="nas-sw-slide-title">{{ slide.title }}</h2>
            </header>
            <ul class="nas-sw-bullet-list nas-sw-bullet-list--plain">
              <li v-for="(line, li) in slide.lines" :key="li">{{ line }}</li>
            </ul>
          </div>
        </template>
      </article>
    </div>


  </div>
</template>

<script>
import pptSlides from '@/data/nasStorageSoftwarePpt.json';
import { parseAllSlides } from '@/utils/nasStorageSoftwarePptParse.js';

export default {
  name: 'NasStorageSoftware',
  data() {
    return {
      slides: parseAllSlides(pptSlides),
    };
  },
  mounted() {
    document.title = '芯存 XNAS 软件 | 瑞和云图';
  },
};
</script>

<style scoped>
/* 参考 SmartX 等官网产品页：白底、深蓝标题条、幻灯片式卡片 */
.nas-sw-page {
  --nas-sw-primary: #0056b3;
  --nas-sw-primary-dark: #003d80;
  --nas-sw-head-bg: linear-gradient(90deg, #0056b3 0%, #0066cc 100%);
  --nas-sw-text: #1a1a1a;
  --nas-sw-muted: #4b5563;
  --nas-sw-border: #e5e7eb;
  --nas-link: #0066cc;

  max-width: 960px;
  margin: 0 auto;
  padding: 2rem 1.5rem 3.5rem;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'PingFang SC',
    'Microsoft YaHei', sans-serif;
  text-align: left;
}

.nas-sw-back {
  display: inline-block;
  font-size: 14px;
  color: var(--nas-link);
  text-decoration: none;
  margin-bottom: 1.25rem;
}

.nas-sw-back:hover {
  text-decoration: underline;
}

.nas-sw-hero {
  margin-bottom: 1.25rem;
}

.nas-sw-deck {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.nas-sw-slide {
  margin: 0;
}

.nas-sw-slide-inner {
  background: #fff;
  border: 1px solid var(--nas-sw-border);
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

/* 封面页 */
.nas-sw-slide--cover {
  padding: 2.5rem 2rem 2.75rem;
  border-top: 4px solid var(--nas-sw-primary);
}

.nas-sw-cover-title {
  margin: 0;
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  color: var(--nas-sw-text);
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-align: left;
}

/* 章节页 */
.nas-sw-slide--section {
  position: relative;
  padding: 2rem 2rem 2.25rem;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-top: 4px solid var(--nas-sw-primary);
}

.nas-sw-section-page {
  position: absolute;
  top: 1.25rem;
  right: 1.5rem;
  font-size: 13px;
  font-weight: 600;
  color: var(--nas-sw-primary);
  opacity: 0.85;
}

.nas-sw-section-title {
  margin: 0;
  padding-right: 3rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--nas-sw-text);
  line-height: 1.4;
  text-align: left;
}

/* 内容页通用标题条（类似 PPT 顶栏） */
.nas-sw-slide-head {
  margin: 0;
  padding: 0.85rem 1.25rem;
  background: var(--nas-sw-head-bg);
  border: none;
}

.nas-sw-slide-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.45;
  text-align: left;
  letter-spacing: 0.01em;
}

.nas-sw-slide--numbered .nas-sw-slide-inner,
.nas-sw-slide--grouped .nas-sw-slide-inner,
.nas-sw-slide--pairs .nas-sw-slide-inner,
.nas-sw-slide--fallback .nas-sw-slide-inner {
  padding-bottom: 0;
}

.nas-sw-slide--numbered .nas-sw-numbered-grid,
.nas-sw-slide--grouped .nas-sw-group-sections,
.nas-sw-slide--pairs .nas-sw-pairs-grid,
.nas-sw-slide--fallback .nas-sw-bullet-list {
  padding: 1.25rem 1.25rem 1.5rem;
}

/* 编号卡片网格 */
.nas-sw-numbered-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.nas-sw-numbered-card {
  padding: 1rem 1rem 1.1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e8edf3;
}

.nas-sw-numbered-num {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: var(--nas-sw-primary);
  letter-spacing: 0.04em;
  margin-bottom: 0.35rem;
}

.nas-sw-numbered-headline {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--nas-sw-text);
  line-height: 1.35;
  text-align: left;
}

.nas-sw-numbered-body {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--nas-sw-muted);
  text-align: left;
}

/* 分组 + 横幅 */
.nas-sw-group-banner {
  margin: 0;
  padding: 0.75rem 1.25rem;
  font-size: 15px;
  font-weight: 600;
  color: var(--nas-sw-primary-dark);
  background: rgba(0, 86, 179, 0.06);
  border-bottom: 1px solid var(--nas-sw-border);
  text-align: left;
}

.nas-sw-group-sections {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.nas-sw-group-block {
  margin: 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f2f5;
}

.nas-sw-group-block:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.nas-sw-group-subtitle {
  margin: 0 0 0.5rem;
  font-size: 15px;
  font-weight: 700;
  color: var(--nas-sw-primary);
  line-height: 1.35;
  text-align: left;
}

.nas-sw-bullet-list {
  margin: 0;
  padding-left: 1.2rem;
  list-style: disc;
}

.nas-sw-bullet-list li {
  margin-bottom: 0.4rem;
  font-size: 14px;
  line-height: 1.65;
  color: var(--nas-sw-muted);
  text-align: left;
}

.nas-sw-bullet-list li:last-child {
  margin-bottom: 0;
}

.nas-sw-bullet-list--plain {
  list-style: none;
  padding-left: 0;
}

.nas-sw-bullet-list--plain li {
  position: relative;
  padding-left: 1rem;
  margin-bottom: 0.5rem;
}

.nas-sw-bullet-list--plain li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--nas-sw-primary);
}

/* 成对卡片 */
.nas-sw-pairs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
}

.nas-sw-pair-card {
  padding: 1rem;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px solid #eef0f3;
}

.nas-sw-pair-sub {
  margin: 0 0 0.45rem;
  font-size: 14px;
  font-weight: 700;
  color: var(--nas-sw-text);
  text-align: left;
}

.nas-sw-pair-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: var(--nas-sw-muted);
  text-align: left;
}

@media (max-width: 640px) {
  .nas-sw-numbered-grid {
    grid-template-columns: 1fr;
  }

  .nas-sw-pairs-grid {
    grid-template-columns: 1fr;
  }
}
</style>
